// PDF rendering and document handling utilities for PdfTableViewer

/**
 * Load PDF document from blob
 */
export const loadPdfDocument = async (pdfBlob, setPdfDoc, setTotalPages) => {
  try {
    const pdfUrl = URL.createObjectURL(pdfBlob);
    const loadingTask = window.pdfjsLib.getDocument({
      url: pdfUrl,
      disableAutoFetch: false,
      disableStream: false,
    });

    const pdf = await loadingTask.promise;
    setPdfDoc(pdf);
    setTotalPages(pdf.numPages);

    URL.revokeObjectURL(pdfUrl);
  } catch (error) {
    // Error loading PDF
  }
};

/**
 * Clear all highlights from the PDF
 */
export const clearHighlights = (setHighlights) => {
  setHighlights([]);
};

/**
 * Highlight words on the PDF with proper coordinate transformations
 */
export const highlightWordsOnPdf = (
  cellInfo,
  currentPage,
  canvasRef,
  highlightContainerRef,
  pdfPageDimensions,
  setHighlights,
  specificWordIndex = null
) => {
  clearHighlights(setHighlights);

  if (
    !cellInfo.words ||
    cellInfo.page !== currentPage ||
    !canvasRef.current ||
    !highlightContainerRef.current
  ) {
    return;
  }

  const canvas = canvasRef.current;
  const canvasRect = canvas.getBoundingClientRect();
  const containerRect = highlightContainerRef.current.getBoundingClientRect();

  // Get PDF page dimensions - these should be stored when the PDF page is rendered
  const { width: pdfWidth, height: pdfHeight } = pdfPageDimensions.current;

  // Calculate scale factors from PDF coordinates to the actual canvas buffer
  const pdfToCanvasScaleX = canvas.width / pdfWidth;
  const pdfToCanvasScaleY = canvas.height / pdfHeight;

  // Calculate CSS scaling factor (canvas buffer size vs displayed size)
  const cssScaleX = canvasRect.width / canvas.width;
  const cssScaleY = canvasRect.height / canvas.height;

  // Combined scaling from PDF coordinates to screen coordinates
  const scaleX = pdfToCanvasScaleX * cssScaleX;
  const scaleY = pdfToCanvasScaleY * cssScaleY;

  // Debug: Highlighting words

  // If specific word index is provided, only highlight that word
  const wordsToHighlight =
    specificWordIndex !== null
      ? [
          {
            ...cellInfo.words[specificWordIndex],
            originalIndex: specificWordIndex,
          },
        ]
      : cellInfo.words.map((word, index) => ({
          ...word,
          originalIndex: index,
        }));

  const newHighlights = wordsToHighlight
    .map((word) => {
      const bbox = word.bbox;
      if (!bbox) {
        // Debug: Skipping word without bbox
        return null;
      }

      // Convert PDF coordinates to canvas coordinates (backend always provides absolute coordinates)
      let x0, y0, x1, y1;

      if (
        bbox.left !== undefined &&
        bbox.top !== undefined &&
        bbox.width !== undefined &&
        bbox.height !== undefined
      ) {
        // {left, top, width, height} format - convert to absolute coordinates
        x0 = bbox.left;
        y0 = bbox.top;
        x1 = bbox.left + bbox.width;
        y1 = bbox.top + bbox.height;
      } else {
        // Standard {x0, y0, x1, y1} format
        x0 = bbox.x0 || bbox.x || 0;
        y0 = bbox.y0 || bbox.y || 0;
        x1 = bbox.x1 || bbox.right || 0;
        y1 = bbox.y1 || bbox.bottom || 0;
      }

      // Now scale from PDF coordinates to canvas coordinates
      const canvasX = x0 * scaleX;
      const canvasY = y0 * scaleY;
      const canvasWidth = (x1 - x0) * scaleX;
      const canvasHeight = (y1 - y0) * scaleY;

      // Position relative to the canvas within the container
      // Use canvas offset within the container for precise positioning
      const canvasOffsetX = canvasRect.left - containerRect.left;
      const canvasOffsetY = canvasRect.top - containerRect.top;

      const x = canvasOffsetX + canvasX;
      const y = canvasOffsetY + canvasY;

      const isSelected =
        specificWordIndex !== null && word.originalIndex === specificWordIndex;

      return {
        id: `highlight-${word.originalIndex}`,
        left: x,
        top: y,
        width: canvasWidth,
        height: canvasHeight,
        text: word.text,
        confidence: word.confidence,
        isSelected: isSelected,
      };
    })
    .filter(Boolean);

  // Debug: Setting highlights
  setHighlights(newHighlights);
};

/**
 * Render a PDF page on the canvas
 */
export const renderPdfPage = async (
  pdfDoc,
  canvasRef,
  currentPage,
  pdfScale,
  pdfPageDimensions,
  selectedCell,
  selectedWordIndex,
  highlightWordsOnPdfCallback
) => {
  if (!pdfDoc || !canvasRef.current) return;

  try {
    const canvas = canvasRef.current;

    // Check if canvas container has proper dimensions
    const canvasRect = canvas.getBoundingClientRect();
    if (canvasRect.width === 0 || canvasRect.height === 0) {
      // Canvas not properly sized yet, skip rendering
      return;
    }

    const page = await pdfDoc.getPage(currentPage);

    // Get the viewport with natural rotation
    const viewport = page.getViewport({
      scale: pdfScale,
      rotation: page.rotate,
    });

    // Store original dimensions with natural rotation
    const originalViewport = page.getViewport({
      scale: 1.0,
      rotation: page.rotate,
    });
    pdfPageDimensions.current = {
      width: originalViewport.width,
      height: originalViewport.height,
    };

    const context = canvas.getContext("2d");

    // Ensure canvas has proper dimensions
    canvas.width = viewport.width;
    canvas.height = viewport.height;

    // Clear any previous content
    context.clearRect(0, 0, canvas.width, canvas.height);

    // Render the page with the viewport
    await page.render({
      canvasContext: context,
      viewport: viewport,
    }).promise;

    // Re-apply highlights
    if (selectedCell && highlightWordsOnPdfCallback) {
      highlightWordsOnPdfCallback(selectedCell, selectedWordIndex);
    }
  } catch (error) {
    // Error rendering PDF page
  }
};
